import { FC, ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded'
import { Box, Paper, Typography, alpha, useTheme } from '@mui/material'
import type { Theme } from '@mui/material'
import { common } from '@mui/material/colors'

import { borderRadiusDefault } from '../../../../themes/theme'
import { formatSeconds } from '../../helpers/scoreDisplay'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { MotivationBeat, MotivationBeatTone, MotivationNarrativePattern } from '../../types'

interface MotivationPatternGuideProps {
  /** Recommended patterns + beat map for the motivation. */
  narrativePattern: MotivationNarrativePattern
  /** Display label of the motivation (already formatted). */
  motivationLabel: string
  /** Display name of the selected game. */
  gameName: string
  /** Display label of the game's genre. */
  genre: string
  /** Accent colour for the step numbers / pattern bars. */
  accent: string
  /**
   * When set, switches from the genre-benchmark framing (single-game Blind
   * Spot) to a comparison framing: the blueprint is drawn from `leaderName`'s
   * creatives and meant to be applied by `followerName`. Used by the Diverging
   * Motivation drilldown so the advice references the actual games being
   * compared instead of a generic genre average.
   */
  comparison?: { leaderName: string; followerName: string }
}

const formatBeatTime = (beat: MotivationBeat): string => `${Math.round(beat.startSecond)}–${formatSeconds(beat.endSecond)}`

/** Map a beat's semantic tone onto the theme palette. */
function beatColor(theme: Theme, tone: MotivationBeatTone): string {
  switch (tone) {
    case 'hook':
      return theme.palette.secondary.main
    case 'demo':
      return theme.palette.primary.main
    case 'peak':
      return theme.palette.success.main
    case 'cta':
      return theme.palette.warning.main
    default:
      return theme.palette.primary.main
  }
}

/**
 * "Carry it with the right pattern" block for the Blind Spot dialog. Surfaces
 * the recommended narrative pattern for a motivation (Step 1, a real pattern-mix
 * bar chart) and how the motivation maps onto that pattern's beats (Step 2, a
 * timed beat strip with on-screen ingredients and a peak callout).
 */
export const MotivationPatternGuide: FC<MotivationPatternGuideProps> = ({ narrativePattern, motivationLabel, gameName, genre, accent, comparison }) => {
  const { t } = useTranslation('ua-insights')
  const { patternLabel, beatLabel } = useUATaxonomy()
  const theme = useTheme()
  const { recommendedPattern, gameCreativesCount, patternShares, avgDurationSeconds, referenceCreativesCount, beats } = narrativePattern
  const recommendedPatternLabel = patternLabel(recommendedPattern)
  // Highlight the peak callout in the same colour as the actual peak beat block
  // (its tone varies by recommended pattern), not a fixed accent.
  const peakBeat = beats.find((b) => b.peak)
  const peakColor = peakBeat ? beatColor(theme, peakBeat.tone) : theme.palette.success.main

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MovieFilterRoundedIcon fontSize="small" sx={{ color: accent }} />
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {t('blind_spot_pattern_guide_heading', { motivation: motivationLabel })}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {comparison ? t('compare_pattern_guide_subheading', { leader: comparison.leaderName }) : t('blind_spot_pattern_guide_subheading', { genre })}
        </Typography>
      </Box>
      <StepCard accent={accent} step={1} title={t('blind_spot_pattern_step1_title')}>
        <Typography variant="body2" sx={{ color: 'text.primary', mb: 1.25 }}>
          {comparison ? (
            <Trans
              i18nKey="ua-insights:compare_pattern_reco"
              values={{
                motivation: motivationLabel,
                pattern: recommendedPatternLabel,
                leader: comparison.leaderName,
                follower: comparison.followerName,
                count: gameCreativesCount,
              }}
              components={{ strong: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }} /> }}
            />
          ) : (
            <Trans
              i18nKey="ua-insights:blind_spot_pattern_reco"
              values={{ motivation: motivationLabel, pattern: recommendedPatternLabel, genre, game: gameName, count: gameCreativesCount }}
              components={{ strong: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }} /> }}
            />
          )}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {patternShares.map((share) => (
            <PatternBar key={share.pattern.id} label={patternLabel(share.pattern)} pct={share.sharePct} color={accent} />
          ))}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1, lineHeight: 1.4 }}>
          {comparison
            ? t('compare_pattern_step1_metric', { motivation: motivationLabel, leader: comparison.leaderName })
            : t('blind_spot_pattern_step1_metric', { motivation: motivationLabel, genre })}
        </Typography>
      </StepCard>

      <StepCard
        accent={accent}
        step={2}
        title={t('blind_spot_pattern_step2_title')}
        meta={t('blind_spot_pattern_step2_meta', {
          pattern: recommendedPatternLabel,
          duration: formatSeconds(avgDurationSeconds),
          count: referenceCreativesCount,
        })}
      >
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'stretch' }}>
          {beats.map((beat) => (
            <BeatBlock key={beat.label} beat={beat} label={beatLabel(beat.label)} color={beatColor(theme, beat.tone)} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
          {beats.map((beat) => (
            <Box key={beat.label} sx={{ flex: `${beat.weight} 1 0`, px: 0.5, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: beat.peak ? 'text.primary' : 'text.secondary', fontWeight: beat.peak ? 700 : 400, lineHeight: 1.35 }}
              >
                {beat.ingredient}
              </Typography>
            </Box>
          ))}
        </Box>
        {peakBeat && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1, lineHeight: 1.4 }}>
            <Trans
              i18nKey="ua-insights:blind_spot_pattern_peak_caption"
              values={{ motivation: motivationLabel, beat: beatLabel(peakBeat.label) }}
              components={{
                strong: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }} />,
                peak: <Box component="span" sx={{ fontWeight: 700, color: peakColor }} />,
                tag: (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      ml: 0.5,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 0.75,
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: peakColor,
                      backgroundColor: alpha(peakColor, 0.12),
                    }}
                  />
                ),
              }}
            />
          </Typography>
        )}
      </StepCard>
    </Box>
  )
}

interface StepCardProps {
  step: number
  title: string
  accent: string
  meta?: string
  children: ReactNode
}

/** Outlined step container with a numbered accent badge and optional meta line. */
const StepCard: FC<StepCardProps> = ({ step, title, accent, meta, children }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: borderRadiusDefault }}>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: accent,
          color: 'common.white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {title}
      </Typography>
      {meta && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {meta}
        </Typography>
      )}
    </Box>
    {children}
  </Paper>
)

interface PatternBarProps {
  label: string
  pct: number
  color: string
}

/** A single labelled pattern-share track (label · bar · value). */
const PatternBar: FC<PatternBarProps> = ({ label, pct, color }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr 42px', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {label}
    </Typography>
    <Box sx={{ height: 8, borderRadius: 4, backgroundColor: alpha(color, 0.16), overflow: 'hidden' }} role="presentation" aria-hidden>
      <Box sx={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: 4 }} />
    </Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {pct}%
    </Typography>
  </Box>
)

interface BeatBlockProps {
  beat: MotivationBeat
  /** Localised beat name; `beat.label` is the backend's English display text. */
  label: string
  color: string
}

/**
 * One beat segment in the timeline, sized by its weight. Non-peak beats read as
 * calm tinted chips (hairline border, hue-tinted fill); the peak beat — where
 * the motivation lands — is the single solid, shadowed focal point.
 */
const BeatBlock: FC<BeatBlockProps> = ({ beat, label, color }) => {
  const peak = !!beat.peak
  return (
    <Box
      sx={{
        flex: `${beat.weight} 1 0`,
        minWidth: 0,
        minHeight: 56,
        px: 1,
        py: 1,
        borderRadius: 1.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        textAlign: 'center',
        backgroundColor: peak ? color : alpha(color, 0.1),
        color: peak ? 'common.white' : 'text.primary',
        border: peak ? '1px solid transparent' : `1px solid ${alpha(color, 0.22)}`,
        boxShadow: peak ? `0 4px 14px ${alpha(color, 0.4)}` : 'none',
      }}
    >
      <Typography
        sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.6875rem', lineHeight: 1.2, color: peak ? alpha(common.white, 0.85) : 'text.secondary' }}>
        {formatBeatTime(beat)}
      </Typography>
    </Box>
  )
}
